import type { FeatureLike } from "ol/Feature";
import Feature from "ol/Feature";
import RenderFeature, { toFeature } from "ol/render/Feature";
import type MapBrowserEvent from "ol/MapBrowserEvent";
import type Map from "ol/Map";
import type Collection from "ol/Collection";
import type Layer from "ol/layer/Layer";
import type { Source } from "ol/source";
import Select, { SelectEvent } from "ol/interaction/Select";
import { clear } from "ol/obj";
import { getUid } from "ol/util";

/**
 * `ol/interaction/Select` n’accepte que `feature instanceof Feature`.
 * Les couches {@link import("ol/layer/VectorTile").default} exposent des {@link RenderFeature},
 * donc la sélection MVT ne déclenchait jamais rien.
 *
 * Cette sous-classe convertit les hits en `Feature` via {@link toFeature} et adapte le toggle
 * Maj (comparaison par `getId()` car les clones ne sont pas les mêmes références que les tuiles).
 *
 * **Tous les polygones intersectant le pixel** sont collectés : le callback ne retourne jamais de
 * valeur truthy à `forEachFeatureAtPixel`, ce qui laisse OL parcourir tous les candidats (contrairement
 * à `multi: true` sur `Select` qui cassait la détection sur MVT).
 */
/** Accès aux champs internes de `Select` (non exposés en API publique). */
type SelectPrivate = {
  condition_: (e: MapBrowserEvent) => boolean;
  addCondition_: (e: MapBrowserEvent) => boolean;
  removeCondition_: (e: MapBrowserEvent) => boolean;
  toggleCondition_: (e: MapBrowserEvent) => boolean;
  multi_: boolean;
  filter_: (f: FeatureLike, l: Layer<Source>) => boolean;
  hitTolerance_: number;
  layerFilter_: (l: Layer<Source>) => boolean;
  featureLayerAssociation_: Record<string, Layer<Source>>;
  addFeatureLayerAssociation_: (f: Feature, l: Layer<Source>) => void;
  removeFeatureLayerAssociation_: (f: Feature) => void;
};

type ResolvedHit = {
  feature: Feature;
  layer: Layer<Source>;
  raw: FeatureLike;
};

export default class VectorTileAwareSelect extends Select {
  handleEvent(mapBrowserEvent: MapBrowserEvent): boolean {
    const t = this as unknown as SelectPrivate;
    if (!t.condition_(mapBrowserEvent)) {
      return true;
    }
    const add = t.addCondition_(mapBrowserEvent);
    const remove = t.removeCondition_(mapBrowserEvent);
    const toggle = t.toggleCondition_(mapBrowserEvent);
    const set = !add && !remove && !toggle;
    const map = mapBrowserEvent.map;
    const features = this.getFeatures();

    const deselected: Feature[] = [];
    const selected: Feature[] = [];

    if (set) {
      clear(t.featureLayerAssociation_);
      const hits = collectAllHitsAtPixel(map, mapBrowserEvent.pixel, t);
      const deduped = dedupeResolvedHits(hits);
      for (const { feature, layer } of deduped) {
        t.addFeatureLayerAssociation_(feature, layer);
        selected.push(feature);
      }
      for (let i = features.getLength() - 1; i >= 0; --i) {
        const feature = features.item(i);
        const index = selected.indexOf(feature);
        if (index > -1) {
          selected.splice(index, 1);
        } else {
          features.remove(feature);
          deselected.push(feature);
        }
      }
      if (selected.length !== 0) {
        features.extend(selected);
      }
    } else {
      const hits = collectAllHitsAtPixel(map, mapBrowserEvent.pixel, t);
      const deduped = dedupeResolvedHits(hits);
      for (const { feature, layer, raw } of deduped) {
        const equivalent = findCollectionEquivalent(features, raw, feature);
        if ((add || toggle) && !equivalent) {
          t.addFeatureLayerAssociation_(feature, layer);
          selected.push(feature);
        } else if ((remove || toggle) && equivalent) {
          deselected.push(equivalent);
          t.removeFeatureLayerAssociation_(equivalent);
        }
      }
      for (let j = deselected.length - 1; j >= 0; --j) {
        features.remove(deselected[j]!);
      }
      features.extend(selected);
    }
    if (selected.length > 0 || deselected.length > 0) {
      this.dispatchEvent(
        new SelectEvent("select", selected, deselected, mapBrowserEvent),
      );
    }
    return true;
  }
}

/** Ne retourne jamais de valeur truthy : laisse parcourir tous les candidats au pixel. */
function collectAllHitsAtPixel(
  map: Map,
  pixel: number[],
  t: SelectPrivate,
): Array<{ raw: FeatureLike; layer: Layer<Source> }> {
  const hits: Array<{ raw: FeatureLike; layer: Layer<Source> }> = [];
  map.forEachFeatureAtPixel(
    pixel,
    (raw, layer) => {
      if (!layer || !t.filter_(raw, layer)) {
        return undefined;
      }
      hits.push({ raw, layer });
      return undefined;
    },
    {
      layerFilter: t.layerFilter_,
      hitTolerance: t.hitTolerance_,
    },
  );
  return hits;
}

function tagLayerSource(feature: Feature, layer: Layer<Source>): void {
  const cn = typeof layer.getClassName === "function" ? layer.getClassName() ?? "" : "";
  feature.set(
    "_mvtLayerLabel",
    cn.includes("stored") ? "Overrides persistés" : "Référentiel dataset",
  );
}

function dedupeResolvedHits(
  hits: Array<{ raw: FeatureLike; layer: Layer<Source> }>,
): ResolvedHit[] {
  const seen = new Set<string>();
  const out: ResolvedHit[] = [];
  for (const { raw, layer } of hits) {
    const resolved = vectorTileHitToFeature(raw);
    if (!resolved) {
      continue;
    }
    const id = resolved.getId();
    const key =
      id !== undefined ? `id:${String(id)}` : `uid:${getUid(raw)}`;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    tagLayerSource(resolved, layer);
    out.push({ feature: resolved, layer, raw });
  }
  return out;
}

function vectorTileHitToFeature(like: FeatureLike): Feature | null {
  if (like instanceof Feature) {
    return like;
  }
  if (like instanceof RenderFeature) {
    return toFeature(like);
  }
  try {
    return toFeature(like as RenderFeature);
  } catch {
    return null;
  }
}

function hitIdentity(like: FeatureLike): string | number | undefined {
  if (like instanceof Feature) {
    const id = like.getId();
    return id as string | number | undefined;
  }
  if (like instanceof RenderFeature) {
    return like.getId();
  }
  return undefined;
}

function findCollectionEquivalent(
  collection: Collection<Feature>,
  rawHit: FeatureLike,
  resolved: Feature,
): Feature | undefined {
  const arr = collection.getArray();
  if (arr.includes(resolved)) {
    return resolved;
  }
  const id = hitIdentity(rawHit);
  if (id !== undefined) {
    const byId = arr.find((f) => f.getId() === id);
    if (byId) {
      return byId;
    }
  }
  return undefined;
}
